import { GTMEvent, pushToDataLayer } from '../../services/gtmService';

export const FREE_HISTORY_CHECK_TRACKING_SOURCES = [
  'ad_details',
  'saved_ads',
  'before_you_buy',
  'side_panel',
] as const;

export type FreeHistoryCheckTrackingSource =
  (typeof FREE_HISTORY_CHECK_TRACKING_SOURCES)[number];

const TRACKING_SOURCE_KEY = 'freeHistoryCheckTrackingSource';

export const useFreeHistoryCheckAnalytics = () => {
  /**
   * Set the tracking source in localStorage
   */
  const setTrackingSource = (source: FreeHistoryCheckTrackingSource) => {
    try {
      localStorage.setItem(TRACKING_SOURCE_KEY, source);
    } catch (error) {
      console.error('Failed to set tracking source in localStorage:', error);
    }
  };

  /**
   * Get the tracking source from localStorage
   */
  const getTrackingSource = (): FreeHistoryCheckTrackingSource | null => {
    try {
      const source = localStorage.getItem(TRACKING_SOURCE_KEY);
      // Validate the source is one of the expected values
      if (
        !(
          source &&
          FREE_HISTORY_CHECK_TRACKING_SOURCES.includes(
            source as FreeHistoryCheckTrackingSource,
          )
        )
      ) {
        return null;
      }

      return source as FreeHistoryCheckTrackingSource;
    } catch (error) {
      console.error('Failed to get tracking source from localStorage:', error);
      return null;
    }
  };

  /**
   * Fires on clicking on "get free full report" within Free HC modal
   * Modal could have been opened from any source (info panel, saved ads, side panel)
   */
  const trackFreeReportModalClick = (gtmTrackingData: GTMEvent = {}) => {
    pushToDataLayer({
      ...gtmTrackingData,
      event: 'free_report_modal_click',
      eventAction: getTrackingSource(),
    });
  };

  /**
   * Fires on clicking "Learn about greenlight"
   */
  const trackLearnAboutGreenlightClick = (gtmTrackingData: GTMEvent = {}) => {
    pushToDataLayer({
      ...gtmTrackingData,
      event: 'link_learn_about_greenlight_click',
      event_category: 'free_history_check',
    });
  };

  /**
   * Fires on clicking on info icon next to cat A & B
   */
  const trackWriteOffModalOpen = (gtmTrackingData: GTMEvent = {}) => {
    pushToDataLayer({
      ...gtmTrackingData,
      event: 'cat_a_b_click',
      event_category: 'free_history_check',
    });
  };

  /**
   * Fires on clicking on "get free full report" from any Free HC CTA on ADP, saved ads
   */
  const trackAdFreeHistoryCheckIntent = (gtmTrackingData: GTMEvent = {}) => {
    pushToDataLayer({
      ...gtmTrackingData,
      event: 'ad_free_history_check_intent',
      event_category: 'ad_leads',
      event_action: getTrackingSource(),
    });
  };

  /**
   * Fire when History check success screen is shown
   */
  const trackFreeHistoryCheckSuccess = (gtmTrackingData: GTMEvent = {}) => {
    pushToDataLayer({
      ...gtmTrackingData,
      event: 'ad_free_history_check_success',
      event_category: 'ad_leads',
      event_action: getTrackingSource(),
    });
  };

  /**
   * Fires when the error screen is shown after Free HC request
   */
  const trackFreeHistoryCheckError = (gtmTrackingData: GTMEvent = {}) => {
    pushToDataLayer({
      ...gtmTrackingData,
      event: 'free_history_check_report_error',
      event_category: 'free_history_check',
      event_action: getTrackingSource(),
    });
  };

  return {
    setTrackingSource,
    trackFreeReportModalClick,
    trackLearnAboutGreenlightClick,
    trackWriteOffModalOpen,
    trackAdFreeHistoryCheckIntent,
    trackFreeHistoryCheckSuccess,
    trackFreeHistoryCheckError,
  };
};
